import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { makeStyles } from '@material-ui/core/styles';
import { MenuItem, Paper, Select } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import allActions from '../../../actions';
import { unoTheme } from '../../../assets/styles/unoTheme';
import MyTable from '../../utils/MyTable';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { FundAPI } from '../../../API/fund';
import ServerFilesController from '../../../API/server_files';
import CircularProgress from '@material-ui/core/CircularProgress';

const FIRST_YEAR = 2026;

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',

        '& .MuiTableContainer-root': {
            height: 'calc(100vh - 103px) !important',
        }
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(3),
        borderBottom: 'solid 1px rgba(0, 0, 0, 0.12)',
        backgroundColor: unoTheme.hoverTable,
    },
    leftHeader: {
        display: 'flex',
        flexDirection: 'column',
    },
    title: {
        textAlign: 'left',
        fontSize: '20px',
        fontWeight: 'bold',
        color: '#1C2228',
    },
    subtitle: {
        fontSize: '14px',
        color: unoTheme.secondaryColor,
    },
    emptyMessage: {
        padding: theme.spacing(4),
        textAlign: 'center',
        color: unoTheme.secondaryColor,
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4),
        '& .progress': {
            color: unoTheme.mainColor,
        }
    },
    addIcon: {
        color: unoTheme.mainColor,
    },
    doneIcon: {
        color: unoTheme.greenMoney,
    },
    yearSelect: {
        marginLeft: theme.spacing(2),
        '& .MuiSelect-root': {
            paddingTop: '6px',
            paddingBottom: '6px',
        }
    },
    slotProgress: {
        width: '18px !important',
        height: '18px !important',
        color: unoTheme.mainColor,
    },
}));

function getKey(fundId, month) {
    return `${fundId}-${month}`;
}

export default function FundsFromInstituition({ content }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);

    const { data: instituition } = content;

    const currentYear = new Date().getFullYear();
    const availableYears = Array.from(
        { length: currentYear - FIRST_YEAR + 1 },
        (_, i) => FIRST_YEAR + i
    );

    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [funds, setFunds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [checkingFiles, setCheckingFiles] = useState(false);
    const [slotStatus, setSlotStatus] = useState({});
    const [uploadTarget, setUploadTarget] = useState(null);

    useEffect(() => {
        async function loadFunds() {
            setLoading(true);
            const response = await FundAPI.getFundsByInstituitionId(instituition.id);
            if (response.success) {
                setFunds(response.body.rows);
            }
            setLoading(false);
        }
        loadFunds();
    }, []);

    useEffect(() => {
        if (funds.length === 0) return;
        checkExistingFiles(funds, selectedYear);
    }, [selectedYear, funds]);

    async function checkExistingFiles(fundList, year) {
        setSlotStatus({});
        setCheckingFiles(true);

        try {
            const basePath = `xmls/${year}/${instituition.id}/`;
            const dirResponse = await ServerFilesController.getFilesByDir({ dir: basePath });

            if (!dirResponse.success || !dirResponse.body.files?.length) return;

            const fundDirs = dirResponse.body.files.filter(f => f.isDir);
            if (!fundDirs.length) return;

            const newStatus = {};

            await Promise.all(fundDirs.map(async (fundDir) => {
                const fundId = fundDir.fileName;
                const monthResponse = await ServerFilesController.getFilesByDir({ dir: fundDir.path });
                if (!monthResponse.success || !monthResponse.body.files?.length) return;

                monthResponse.body.files.filter(f => f.isDir).forEach(monthDir => {
                    const month = monthDir.fileName;
                    if (month === 'jun' || month === 'dez') {
                        newStatus[getKey(fundId, month)] = 'done';
                    }
                });
            }));

            setSlotStatus(newStatus);
        } finally {
            setCheckingFiles(false);
        }
    }

    function handleIconClick(fund, month) {
        setUploadTarget({ fund, month });
        fileInputRef.current.click();
    }

    async function handleFileChange(e) {
        const files = Array.from(e.target.files);
        if (!files.length || !uploadTarget) return;

        const { fund, month } = uploadTarget;
        const key = getKey(fund.id, month);
        const path = `xmls/${selectedYear}/${instituition.id}/${fund.id}/${month}/`;

        setSlotStatus(prev => ({ ...prev, [key]: 'uploading' }));

        try {
            const formData = new FormData();
            files.forEach(f => formData.append('files', f));
            formData.append('path', path);

            await ServerFilesController.uploadFiles(formData);

            setSlotStatus(prev => ({ ...prev, [key]: 'done' }));
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                type: 'success',
                message: 'XML enviado com sucesso!',
            }));
        } catch (err) {
            setSlotStatus(prev => ({ ...prev, [key]: 'idle' }));
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                type: 'error',
                message: 'Erro ao enviar o XML: ' + (err?.response?.data?.message || err.message),
            }));
        }

        if (fileInputRef.current) fileInputRef.current.value = '';
        setUploadTarget(null);
    }

    function renderSlotCell(fund, month) {
        const key = getKey(fund.id, month);
        const status = slotStatus[key] || 'idle';

        if (checkingFiles) {
            return (
                <IconButton size="small" disabled>
                    <CircularProgress className={classes.slotProgress} />
                </IconButton>
            );
        }

        if (status === 'uploading') {
            return (
                <IconButton size="small" disabled>
                    <CircularProgress className={classes.slotProgress} />
                </IconButton>
            );
        }
        if (status === 'done') {
            return (
                <IconButton
                    size="small"
                    className={classes.doneIcon}
                    onClick={() => handleIconClick(fund, month)}>
                    <CheckCircleIcon fontSize="small" />
                </IconButton>
            );
        }
        return (
            <IconButton
                size="small"
                className={classes.addIcon}
                onClick={() => handleIconClick(fund, month)}>
                <AddIcon fontSize="small" />
            </IconButton>
        );
    }

    return (
        <div className={classes.root}>

            <input
                ref={fileInputRef}
                type="file"
                accept=".xml"
                hidden
                onChange={handleFileChange}
            />

            <Paper style={{ marginBottom: '4px' }}>
                <div className={classes.header}>
                    <div className={classes.leftHeader}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Typography className={classes.title}>
                                Fundos
                            </Typography>
                            <Select
                                className={classes.yearSelect}
                                variant="outlined"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}>
                                {availableYears.map((year) => (
                                    <MenuItem key={year} value={year}>{year}</MenuItem>
                                ))}
                            </Select>
                        </div>
                        <Typography className={classes.subtitle}>
                            {instituition.description}
                        </Typography>
                    </div>

                    <IconButton onClick={() => dispatch(allActions.rightContentActions.setRightContent(null))}>
                        <CloseIcon />
                    </IconButton>
                </div>
            </Paper>

            {loading ? (
                <div className={classes.loadingContainer}>
                    <CircularProgress className={'progress'} />
                </div>
            ) : funds.length === 0 ? (
                <Typography className={classes.emptyMessage}>
                    Nenhum fundo encontrado para esta instituição.
                </Typography>
            ) : (
                <MyTable stickyHeader size="small">
                    <colgroup>
                        <col width="40%" />
                        <col width="30%" />
                        <col width="15%" />
                        <col width="15%" />
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell align="left">Nome</TableCell>
                            <TableCell align="center">CNPJ</TableCell>
                            <TableCell align="center">Jun (XML)</TableCell>
                            <TableCell align="center">Dez (XML)</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {funds.map((fund) => (
                            <TableRow key={'fund-inst-' + fund.id}>
                                <TableCell align="left">{fund.fund_name}</TableCell>
                                <TableCell align="center">{fund.cnpj}</TableCell>
                                <TableCell align="center">
                                    {renderSlotCell(fund, 'jun')}
                                </TableCell>
                                <TableCell align="center">
                                    {renderSlotCell(fund, 'dez')}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </MyTable>
            )}

        </div>
    );
}
